import React, { useState, useEffect } from "react";
import {
  Badge,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Box,
  Divider,
  ListItemText,
  Chip,
  Tooltip,
  alpha,
} from "@mui/material";
import InboxIcon from "@mui/icons-material/MoveToInbox";
import DeleteIcon from "@mui/icons-material/Clear";
import PublicIcon from "@mui/icons-material/Public";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import * as signalR from "@microsoft/signalr";
import {
  getNotificacionesBandeja,
  marcarNotificacionBandejaLeida,
  marcarTodasBandejaLeidas,
  eliminarNotificacionBandeja,
} from "../http/actions/NotificacionBandejaAction";
import { useNavigate } from "react-router-dom";

const NotificacionBandejaMenu = () => {
  const navigate = useNavigate();
  const [notificaciones, setNotificaciones] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const cargarNotificaciones = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("token");
        const data = await getNotificacionesBandeja(token);
        setNotificaciones(data);
      } catch (error) {
        console.error("Error cargando notificaciones de bandeja:", error);
      } finally {
        setLoading(false);
      }
    };

    cargarNotificaciones();

    const baseUrl = process.env.REACT_APP_BACKEND_URL;

    const conn = new signalR.HubConnectionBuilder()
      .withUrl(`${baseUrl}/notificacionHub`, {
        accessTokenFactory: () => localStorage.getItem("token") || "",
        withCredentials: true,
      })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    conn
      .start()
      .then(() => {
        conn.on("RecibirNotificacionBandeja", (data) => {
          setNotificaciones((prev) => [data, ...prev]);
        });
      })
      .catch((err) => console.error("Error SignalR NotificacionBandeja:", err));

    return () => conn.stop();
  }, []);

  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleNotificacionClick = async (notificacion) => {
    try {
      const token = localStorage.getItem("token");
      await marcarNotificacionBandejaLeida(token, notificacion.id);
      setNotificaciones((prev) =>
        prev.map((n) => (n.id === notificacion.id ? { ...n, leida: true } : n))
      );
      handleClose();
      navigate(`/bandeja/normativa/${notificacion.normaScraperId}`);
    } catch (error) {
      console.error("Error al marcar como leída:", error);
    }
  };

  const handleMarcarTodasLeidas = async () => {
    try {
      const token = localStorage.getItem("token");
      await marcarTodasBandejaLeidas(token);
      setNotificaciones((prev) => prev.map((n) => ({ ...n, leida: true })));
      handleClose();
    } catch (error) {
      console.error("Error al marcar todas como leídas:", error);
    }
  };

  const handleEliminar = async (event, id) => {
    event.stopPropagation();
    try {
      const token = localStorage.getItem("token");
      await eliminarNotificacionBandeja(token, id);
      setNotificaciones((prev) => prev.filter((n) => n.id !== id));
    } catch (error) {
      console.error("Error al eliminar notificación de bandeja:", error);
    }
  };

  const noLeidas = notificaciones.filter((n) => !n.leida).length;

  return (
    <>
      <Tooltip title="Nuevas normas scrapeadas">
        <IconButton
          onClick={handleClick}
          color="inherit"
          sx={{ mr: 1 }}
          aria-label="notificaciones bandeja scraper"
        >
          <Badge badgeContent={noLeidas} color="error">
            <InboxIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          style: { maxHeight: 450, width: 360 },
        }}
      >
        <Box
          sx={{
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            bgcolor: "background.paper",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <InboxIcon sx={{ color: "text.primary", fontSize: 20 }} />
            <Typography variant="h6" fontSize={15} fontWeight={600}>
              Bandeja Scraper
            </Typography>
          </Box>
          {noLeidas > 0 && (
            <Typography
              variant="body2"
              color="primary"
              sx={{ cursor: "pointer", fontWeight: 500 }}
              onClick={handleMarcarTodasLeidas}
            >
              Marcar todas leídas
            </Typography>
          )}
        </Box>
        <Divider />

        {loading ? (
          <MenuItem>
            <Typography variant="body2" color="text.secondary">
              Cargando...
            </Typography>
          </MenuItem>
        ) : notificaciones.length === 0 ? (
          <Box
            sx={{
              p: 3,
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
            }}
          >
            <InboxIcon sx={{ color: "text.disabled", fontSize: 36 }} />
            <Typography variant="body2" color="text.secondary">
              Sin normas nuevas
            </Typography>
          </Box>
        ) : (
          notificaciones.map((notif) => (
            <MenuItem
              key={notif.id}
              onClick={() => handleNotificacionClick(notif)}
              sx={{
                backgroundColor: notif.leida
                  ? "inherit"
                  : (t) => alpha(t.palette.primary.main, 0.10),
                display: "block",
                position: "relative",
                paddingRight: "48px",
                borderLeft: notif.leida
                  ? "3px solid transparent"
                  : (t) => `3px solid ${t.palette.primary.main}`,
                "&:hover": {
                  backgroundColor: (t) => alpha(t.palette.primary.main, 0.14),
                },
              }}
            >
              <ListItemText
                primary={
                  <Typography
                    variant="body2"
                    fontWeight={notif.leida ? 400 : 600}
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      pr: 1,
                    }}
                  >
                    {notif.nombreNorma || notif.mensaje}
                  </Typography>
                }
                secondary={
                  <Box
                    sx={{
                      display: "flex",
                      gap: 0.5,
                      flexWrap: "wrap",
                      mt: 0.5,
                    }}
                  >
                    {notif.regulador && (
                      <Chip
                        icon={<AccountBalanceIcon sx={{ fontSize: "12px !important" }} />}
                        label={notif.regulador}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: 10, height: 20 }}
                      />
                    )}
                    {notif.pais && (
                      <Chip
                        icon={<PublicIcon sx={{ fontSize: "12px !important" }} />}
                        label={notif.pais}
                        size="small"
                        variant="outlined"
                        color="info"
                        sx={{ fontSize: 10, height: 20 }}
                      />
                    )}
                    <Typography
                      variant="caption"
                      color="text.disabled"
                      sx={{ alignSelf: "center", ml: 0.5 }}
                    >
                      {new Date(notif.fechaCreacion + "Z").toLocaleString("es-CL", {
                        dateStyle: "short",
                        timeStyle: "short",
                      })}
                    </Typography>
                  </Box>
                }
              />

              <IconButton
                size="small"
                onClick={(e) => handleEliminar(e, notif.id)}
                sx={{
                  position: "absolute",
                  right: 6,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "text.disabled",
                  "&:hover": { color: "error.main" },
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </MenuItem>
          ))
        )}
      </Menu>
    </>
  );
};

export default NotificacionBandejaMenu;